import { forwardRef, useState, useEffect } from "react";
import { routeUrl } from "../utils/paths";
import { appendFallbackSrcSet, buildSrcSet } from "../utils/images";

/**
 * Project card component displaying project preview.
 * Uses responsive images with srcSet for optimal performance.
 * Memoized with forwardRef for controlled parent animations.
 */
interface ProjectCardProps {
  id: string;
  index: number;
  title: string;
  subtitle: string;
  imagePc: string;
  deviceRef?: React.Ref<HTMLDivElement>;
}

const ProjectCard = forwardRef<HTMLAnchorElement, ProjectCardProps>(
  ({ id, index, title, subtitle, imagePc, deviceRef }, ref) => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
      const mediaQuery = window.matchMedia("(max-width: 900px)");
      setIsMobile(mediaQuery.matches);

      const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
      mediaQuery.addEventListener("change", handler);
      return () => mediaQuery.removeEventListener("change", handler);
    }, []);

    const displayTitle = isMobile ? title.split(" ").slice(0, 2).join(" ") : title;

    const numeral = String(index + 1).padStart(2, "0");
    const href = routeUrl(`works/${encodeURIComponent(id)}`);
    const srcSet = appendFallbackSrcSet(buildSrcSet(imagePc), imagePc);
    const imageSizes = "(max-width: 900px) 92vw, 55vw";

    return (
      <a
        className="latest-work__item"
        href={href}
        aria-label={`View ${title} project`}
        ref={ref}
      >
        <div className="latest-work__left">
          <span className="latest-work__index" aria-hidden="true">
            {numeral}.
          </span>
          <div className="latest-work__meta">
            <h3 className="latest-work__name">{displayTitle}</h3>
            <p className="latest-work__tagline">{subtitle}</p>
          </div>
        </div>

        <div className="latest-work__right">
          <div className="latest-work__device" ref={deviceRef}>
            <img
              src={imagePc}
              sizes={srcSet ? imageSizes : undefined}
              alt={`${title} preview`}
              width={1400}
              height={900}
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </a>
    );
  }
);

ProjectCard.displayName = "ProjectCard";

export default ProjectCard;
